@import "tailwindcss";

@theme {
  /* Color tokens */
  --color-background: #000000;
  --color-surface: #0b0e10;
  --color-surface-elevated: #151a1d;
  --color-border: #272c2f;
  --color-border-strong: #485054;

  --color-text-primary: #f5f3ed;
  --color-text-secondary: #bdc6c5;
  --color-text-muted: #97a5a5;
  --color-text-inverse: #0b0f11;

  --color-accent: #f0f0ed;
  --color-accent-foreground: #0b0f11;
  --color-accent-hover: #ffffff;
  --color-accent-muted: rgba(240, 240, 237, 0.08);

  --color-success: #10b981;
  --color-warning: #f59e0b;
  --color-error: #ef4444;

  /* Typography tokens */
  --font-sans: "DM Sans", Arial, sans-serif;
  --font-display: "DM Sans", Arial, sans-serif;
  --font-mono: "JetBrains Mono", "Fira Code", monospace;

  /* Spacing tokens */
  --space-4xs: 0.125rem;
  --space-3xs: 0.25rem;
  --space-2xs: 0.375rem;
  --space-xs: 0.5rem;
  --space-sm: 0.75rem;
  --space-md: 1rem;
  --space-lg: 1.5rem;
  --space-xl: 2rem;
  --space-2xl: 3rem;
  --space-3xl: 4rem;
  --space-4xl: 6rem;
  --space-5xl: 8rem;

  /* Border radius tokens */
  --radius-sm: 0.375rem;
  --radius-md: 0.5rem;
  --radius-lg: 0.75rem;
  --radius-xl: 1rem;
  --radius-2xl: 1.5rem;
  --radius-full: 9999px;

  /* Shadow tokens */
  --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  --shadow-lg:
    0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  --shadow-xl:
    0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
  --shadow-2xl: 0 25px 50px -12px rgb(0 0 0 / 0.25);
  --shadow-glow: 0 0 40px -10px rgb(240 240 237 / 0.15);
  --shadow-glow-subtle: 0 0 20px -5px rgb(240 240 237 / 0.08);

  /* Animation tokens */
  --duration-instant: 50ms;
  --duration-fast: 150ms;
  --duration-normal: 250ms;
  --duration-slow: 350ms;
  --duration-slower: 500ms;
}

/* ========================================
   BASE STYLES
   ======================================== */

* {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
  -webkit-text-size-adjust: 100%;
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }

  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

body {
  margin: 0;
  font-family: var(--font-sans);
  font-size: 1rem;
  line-height: 1.7;
  color: var(--color-text-primary);
  background-color: var(--color-background);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

#root {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* ========================================
   TYPOGRAPHY UTILITIES
   ======================================== */

.display-xl {
  font-size: clamp(3.5rem, 8vw, 6rem);
  line-height: 1.05;
  letter-spacing: -0.03em;
  font-weight: 700;
  font-family: var(--font-display);
}
.display-lg {
  font-size: clamp(2.5rem, 5vw, 4rem);
  line-height: 1.1;
  letter-spacing: -0.02em;
  font-weight: 700;
  font-family: var(--font-display);
}
.display-md {
  font-size: clamp(2rem, 4vw, 3rem);
  line-height: 1.15;
  letter-spacing: -0.01em;
  font-weight: 600;
  font-family: var(--font-display);
}
.display-sm {
  font-size: clamp(1.5rem, 3vw, 2.25rem);
  line-height: 1.2;
  letter-spacing: 0;
  font-weight: 600;
  font-family: var(--font-display);
}

.h1 {
  font-size: clamp(2rem, 3.5vw, 2.75rem);
  line-height: 1.2;
  letter-spacing: -0.01em;
  font-weight: 600;
  font-family: var(--font-display);
}
.h2 {
  font-size: clamp(1.5rem, 2.5vw, 2rem);
  line-height: 1.3;
  letter-spacing: 0;
  font-weight: 600;
  font-family: var(--font-display);
}
.h3 {
  font-size: clamp(1.25rem, 2vw, 1.5rem);
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: 600;
  font-family: var(--font-display);
}

.body-lg {
  font-size: 1.125rem;
  line-height: 1.7;
}
.body {
  font-size: 1rem;
  line-height: 1.7;
}
.body-sm {
  font-size: 1rem;
  line-height: 1.65;
}
.caption {
  font-size: 0.75rem;
  line-height: 1.5;
  letter-spacing: 0.02em;
}
.label {
  font-size: 0.75rem;
  line-height: 1.5;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-weight: 600;
}

/* ========================================
   FOCUS STATES
   ======================================== */

*:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

*:focus:not(:focus-visible) {
  outline: none;
}

/* ========================================
   SCROLLBAR STYLING
   ======================================== */

::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: var(--color-background);
}

::-webkit-scrollbar-thumb {
  background: var(--color-border);
  border-radius: var(--radius-full);
}

::-webkit-scrollbar-thumb:hover {
  background: var(--color-border-strong);
}

/* ========================================
   SELECTION STYLING
   ======================================== */

::selection {
  background-color: var(--color-accent);
  color: var(--color-accent-foreground);
}

/* ========================================
   UTILITY CLASSES
   ======================================== */

.container {
  width: 100%;
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 var(--space-lg);
}

@media (min-width: 640px) {
  .container {
    padding: 0 var(--space-xl);
  }
}

@media (min-width: 1024px) {
  .container {
    padding: 0 var(--space-2xl);
  }
}

@media (min-width: 1280px) {
  .container {
    padding: 0 var(--space-3xl);
  }
}

.section {
  padding: var(--space-4xl) 0;
}

@media (min-width: 768px) {
  .section {
    padding: var(--space-5xl) 0;
  }
}

.section-sm {
  padding: var(--space-2xl) 0;
}

@media (min-width: 768px) {
  .section-sm {
    padding: var(--space-3xl) 0;
  }
}

/* Text gradient utility */
.text-gradient {
  background: linear-gradient(
    135deg,
    var(--color-text-primary) 0%,
    var(--color-accent) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.text-gradient-accent {
  background: linear-gradient(
    135deg,
    var(--color-accent) 0%,
    var(--color-accent-hover) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Grid background pattern */
.bg-grid {
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
}

.bg-grid-accent {
  background-image:
    linear-gradient(rgba(0, 212, 170, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 212, 170, 0.05) 1px, transparent 1px);
  background-size: 60px 60px;
}

/* Gradient border */
.gradient-border {
  position: relative;
  background: var(--color-surface);
  border-radius: var(--radius-lg);
}

.gradient-border::before {
  content: "";
  position: absolute;
  inset: -1px;
  border-radius: var(--radius-lg);
  background: linear-gradient(
    135deg,
    var(--color-accent),
    transparent 50%,
    var(--color-border)
  );
  opacity: 0;
  transition: opacity var(--duration-normal) ease;
  z-index: -1;
}

.gradient-border:hover::before {
  opacity: 1;
}

/* Glass morphism */
.glass {
  background: rgba(17, 24, 39, 0.8);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.glass-strong {
  background: rgba(17, 24, 39, 0.95);
  backdrop-filter: blur(40px);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

/* ========================================
   ANIMATION KEYFRAMES
   ======================================== */

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes pulseSubtle {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

@keyframes gradientShift {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes flow {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-fade-in {
  animation: fadeIn var(--duration-normal) ease-out forwards;
}
.animate-slide-up {
  animation: slideUp var(--duration-slower) ease-out forwards;
}
.animate-slide-down {
  animation: slideDown var(--duration-normal) ease-out forwards;
}
.animate-scale-in {
  animation: scaleIn var(--duration-normal) ease-out forwards;
}
.animate-pulse-subtle {
  animation: pulseSubtle 3s ease-in-out infinite;
}
.animate-gradient-shift {
  animation: gradientShift 8s ease-in-out infinite;
}
.animate-flow {
  animation: flow 20s linear infinite;
}
.animate-spin {
  animation: spin 1s linear infinite;
}

/* Staggered animations */
.stagger-1 {
  animation-delay: 0ms;
}
.stagger-2 {
  animation-delay: 100ms;
}
.stagger-3 {
  animation-delay: 200ms;
}
.stagger-4 {
  animation-delay: 300ms;
}
.stagger-5 {
  animation-delay: 400ms;
}
.stagger-6 {
  animation-delay: 500ms;
}
.stagger-7 {
  animation-delay: 600ms;
}
.stagger-8 {
  animation-delay: 700ms;
}

/* ========================================
   FORM STYLES
   ======================================== */

.input-base {
  width: 100%;
  padding: var(--space-sm) var(--space-md);
  font-family: var(--font-sans);
  font-size: 1rem;
  line-height: 1.5;
  color: var(--color-text-primary);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition:
    border-color var(--duration-fast) ease,
    box-shadow var(--duration-fast) ease,
    background-color var(--duration-fast) ease;
}

.input-base:focus {
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px var(--color-accent-muted);
  outline: none;
}

.input-base::placeholder {
  color: var(--color-text-muted);
}

.input-base:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.input-error {
  border-color: var(--color-error);
}

.input-error:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15);
}

.label-base {
  display: block;
  margin-bottom: var(--space-2xs);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-secondary);
}

.error-message {
  margin-top: var(--space-2xs);
  font-size: 0.8125rem;
  color: var(--color-error);
}

/* ========================================
   BUTTON BASE STYLES
   ======================================== */

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-xl);
  font-family: var(--font-sans);
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.5;
  letter-spacing: 0.01em;
  border-radius: var(--radius-md);
  border: none;
  cursor: pointer;
  transition: all var(--duration-fast) ease;
  white-space: nowrap;
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background-color: var(--color-accent);
  color: var(--color-accent-foreground);
  box-shadow: var(--shadow-glow-subtle);
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--color-accent-hover);
  box-shadow: var(--shadow-glow);
  transform: translateY(-1px);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(0);
}

.btn-secondary {
  background-color: transparent;
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
}

.btn-secondary:hover:not(:disabled) {
  background-color: var(--color-surface-elevated);
  border-color: var(--color-border-strong);
}

.btn-ghost {
  background-color: transparent;
  color: var(--color-text-secondary);
}

.btn-ghost:hover:not(:disabled) {
  color: var(--color-text-primary);
  background-color: var(--color-surface-elevated);
}

.btn-outline-accent {
  background-color: transparent;
  color: var(--color-accent);
  border: 1px solid var(--color-accent);
}

.btn-outline-accent:hover:not(:disabled) {
  background-color: var(--color-accent-muted);
}

.btn-lg {
  padding: var(--space-md) var(--space-2xl);
  font-size: 1rem;
}

.btn-sm {
  padding: var(--space-2xs) var(--space-md);
  font-size: 0.8125rem;
}

.btn-icon {
  padding: var(--space-sm);
}

.btn-full {
  width: 100%;
}

/* ========================================
   CARD BASE STYLES
   ======================================== */

.card {
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  transition:
    border-color var(--duration-normal) ease,
    box-shadow var(--duration-normal) ease,
    transform var(--duration-normal) ease;
}

.card:hover {
  border-color: var(--color-border-strong);
  box-shadow: var(--shadow-lg);
}

.card-elevated {
  background-color: var(--color-surface-elevated);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
}

/* ========================================
   LINK STYLES
   ======================================== */

.link {
  color: var(--color-accent);
  text-decoration: none;
  transition: color var(--duration-fast) ease;
}

.link:hover {
  color: var(--color-accent-hover);
  text-decoration: underline;
}

.link-underline {
  position: relative;
  text-decoration: none;
  color: inherit;
}

.link-underline::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 1px;
  background-color: currentColor;
  transform: scaleX(0);
  transform-origin: right;
  transition: transform var(--duration-normal) ease;
}

.link-underline:hover::after {
  transform: scaleX(1);
  transform-origin: left;
}

/* ========================================
   DIVIDER
   ======================================== */

.divider {
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    var(--color-border),
    transparent
  );
  border: none;
}

.divider-strong {
  height: 1px;
  background-color: var(--color-border);
}

/* ========================================
   BADGE/TAG
   ======================================== */

.badge {
  display: inline-flex;
  align-items: center;
  padding: var(--space-3xs) var(--space-sm);
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.5;
  letter-spacing: 0.02em;
  border-radius: var(--radius-full);
  text-transform: uppercase;
}

.badge-primary {
  background-color: var(--color-accent-muted);
  color: var(--color-accent);
}

.badge-secondary {
  background-color: var(--color-surface-elevated);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
}

.badge-success {
  background-color: rgba(16, 185, 129, 0.15);
  color: var(--color-success);
}

.badge-warning {
  background-color: rgba(245, 158, 11, 0.15);
  color: var(--color-warning);
}

/* ========================================
   TOOLTIP
   ======================================== */

.tooltip {
  position: relative;
}

.tooltip::before {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(-8px);
  padding: var(--space-2xs) var(--space-sm);
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-text-inverse);
  background-color: var(--color-text-primary);
  border-radius: var(--radius-sm);
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition:
    opacity var(--duration-fast) ease,
    transform var(--duration-fast) ease,
    visibility var(--duration-fast) ease;
  z-index: var(--z-tooltip);
}

.tooltip:hover::before {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(-4px);
}

/* ========================================
   VISUALLY HIDDEN
   ======================================== */

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Shared editorial system */
:root {
  --color-accent-muted: rgba(240, 240, 237, 0.08);
  --color-accent-foreground: #080a0b;
  --radius-lg: 0.75rem;
  --radius-xl: 1rem;
}
body {
  background: var(--color-background);
}
a {
  text-decoration: none;
}
button,
summary {
  -webkit-tap-highlight-color: transparent;
}
button {
  cursor: pointer;
}
main {
  min-width: 0;
}
section [id] {
  scroll-margin-top: 7rem;
}
.container {
  padding-inline: clamp(1.25rem, 4vw, 3rem);
}
.section {
  padding-block: clamp(4.5rem, 7.5vw, 7rem);
}
.section-title {
  font-size: clamp(2rem, 3.8vw, 3.15rem);
  font-weight: 550;
  line-height: 1.12;
  letter-spacing: -0.045em;
  text-wrap: balance;
}
.section-heading {
  max-width: 46rem;
}
.section-heading .label {
  font-size: 0.75rem;
  margin-bottom: 1.25rem;
  letter-spacing: 0.12em;
}
.section-heading p {
  max-width: 40rem;
  font-size: 1.125rem;
  line-height: 1.75;
}
.section-heading.text-center p {
  margin-inline: auto;
}
.h3 {
  font-size: clamp(1.2rem, 1.8vw, 1.4rem);
  line-height: 1.35;
  letter-spacing: -0.02em;
}
.body,
.body-sm {
  font-size: 1rem;
  line-height: 1.75;
}
.caption {
  font-size: 0.8125rem;
}
.btn {
  min-height: 46px;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
  white-space: normal;
  text-align: center;
  transition:
    background-color 0.2s,
    border-color 0.2s,
    box-shadow 0.2s,
    transform 0.2s;
}
.btn-lg {
  min-height: 54px;
  padding: 0.9rem 1.5rem;
  font-size: 1rem;
}
.btn-sm {
  padding: 0.65rem 1rem;
}
.btn-primary {
  box-shadow: none;
}
.btn-primary:hover:not(:disabled) {
  box-shadow: 0 6px 24px rgb(240 240 237 / 0.13);
}
.btn-secondary {
  border-color: var(--color-border-strong);
}
.btn svg {
  flex-shrink: 0;
}
.card,
.card-elevated {
  box-shadow: none;
}
.card-elevated {
  background: var(--color-surface);
}
.card {
  transition:
    border-color 0.25s ease,
    background 0.25s ease,
    transform 0.25s ease;
}
.card:hover {
  box-shadow: none;
}
.feature-link {
  color: inherit;
  border-radius: var(--radius-lg);
}
.feature-link:hover {
  border-color: var(--color-accent);
}
.feature-link:hover .text-link svg,
.text-link:hover svg {
  transform: translateX(4px);
}
.text-link {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1rem;
  font-weight: 550;
  color: var(--color-accent);
  min-height: 44px;
}
.text-link svg {
  flex-shrink: 0;
  transition: transform 0.2s;
}
.step-number {
  font-family: var(--font-mono);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-accent);
}
.brand-link {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  font-size: 1.4rem;
  font-weight: 650;
  letter-spacing: -0.04em;
  color: var(--color-text-primary);
}
.glass,
.glass-strong {
  background: rgb(11 15 17 / 0.96);
  border-color: var(--color-border);
}

/* Hero and animated system map */
.hero-stage {
  position: relative;
  isolation: isolate;
  padding-top: clamp(4rem, 6vw, 6rem);
  overflow: clip;
  background: radial-gradient(
    ellipse at 76% 48%,
    rgb(240 240 237 / 0.05),
    transparent 60%
  );
}
.hero-grid-bg {
  position: absolute;
  inset: 0;
  z-index: -1;
  background-image:
    linear-gradient(rgb(255 255 255 / 0.018) 1px, transparent 1px),
    linear-gradient(90deg, rgb(255 255 255 / 0.018) 1px, transparent 1px);
  background-size: 80px 80px;
  mask-image: linear-gradient(90deg, transparent 30%, black);
}
.hero-grid {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  align-items: center;
  gap: clamp(2.5rem, 5vw, 5rem);
}
.hero-copy {
  min-width: 0;
  padding-block: 1rem;
}
.hero-kicker {
  display: inline-flex;
  gap: 0.65rem;
  align-items: center;
  margin-bottom: 1.8rem;
  color: var(--color-accent);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.13em;
}
.hero-kicker > span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-accent);
  box-shadow: 0 0 0 4px var(--color-accent-muted);
}
.hero-title {
  font-size: clamp(2.7rem, 4.75vw, 4.25rem);
  line-height: 1.06;
  letter-spacing: -0.055em;
  font-weight: 550;
  text-wrap: balance;
}
.hero-title > span {
  color: var(--color-accent);
}
.hero-description {
  margin-block: 1.8rem 2.25rem;
  max-width: 34rem;
  font-size: 1.15rem;
  line-height: 1.8;
  color: var(--color-text-secondary);
}
.hero-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
}
.hero-secondary {
  display: inline-flex;
  gap: 0.5rem;
  align-items: center;
  min-height: 48px;
  color: var(--color-text-primary);
  font-weight: 550;
}
.hero-secondary:hover {
  color: var(--color-accent);
}
.hero-note {
  margin-top: 1.5rem;
  color: var(--color-text-muted);
  font-size: 0.875rem;
}
.hero-visual {
  position: relative;
  min-width: 0;
  border: 1px solid var(--color-border-strong);
  border-radius: 1rem;
  padding: clamp(1rem, 2.5vw, 1.75rem);
  background: linear-gradient(145deg, rgb(24 22 20 / 0.8), rgb(5 12 14 / 0.86));
  backdrop-filter: blur(14px) saturate(108%);
  box-shadow:
    0 2px 10px rgb(0 0 0 / 0.44),
    inset 0 3px 3px rgb(255 255 255 / 0.035);
}
.architecture-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.75rem;
}
.architecture-header .label {
  color: var(--color-accent);
  font-size: 0.7rem;
  letter-spacing: 0.12em;
}
.architecture-header p {
  font-size: 1.1rem;
  margin-top: 0.35rem;
  color: var(--color-text-primary);
}
.architecture-badge {
  font-size: 0.65rem;
  padding: 0.3rem 0.6rem;
  border: 1px solid var(--color-border-strong);
  border-radius: 100px;
  color: var(--color-text-secondary);
  white-space: nowrap;
}
.architecture-stages {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
}
.architecture-stage {
  min-width: 0;
  padding: 1rem;
  border: 1px solid var(--color-border);
  border-radius: 0.5rem;
  background: var(--color-background);
  animation: systemStage 10.8s ease-in-out infinite;
  animation-delay: var(--stage-delay);
}
.architecture-stage-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--color-accent);
  margin-bottom: 0.85rem;
}
.architecture-index {
  color: var(--color-text-muted);
  font-family: var(--font-mono);
  font-size: 0.7rem;
}
.architecture-stage h2 {
  font-size: 0.95rem;
  line-height: 1.4;
  font-weight: 550;
  letter-spacing: -0.015em;
}
.architecture-stage p {
  color: var(--color-text-muted);
  font-size: 0.75rem;
  line-height: 1.5;
  margin-top: 0.3rem;
}
.architecture-output {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1.1rem 0.8rem;
  background: var(--color-accent-muted);
  border: 1px solid rgb(240 240 237 / 0.25);
  border-radius: 0.5rem;
  margin-top: 1rem;
}
.output-icon {
  flex-shrink: 0;
  color: var(--color-accent);
}
.architecture-output .label {
  font-size: 0.6rem;
  letter-spacing: 0.08em;
  color: var(--color-accent);
}
.architecture-output p {
  font-size: 1rem;
  font-weight: 550;
}
.architecture-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 1rem;
  color: var(--color-text-muted);
  font-size: 0.7rem;
}
.architecture-footer > span {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.system-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--color-accent);
}
.architecture-footer button {
  display: inline-flex;
  gap: 0.4rem;
  align-items: center;
  padding: 0.4rem 0.6rem;
  min-height: 36px;
  border: 1px solid var(--color-border);
  border-radius: 0.4rem;
  color: var(--color-text-secondary);
  font-size: 0.7rem;
  background: transparent;
}
.architecture-footer button:hover {
  border-color: var(--color-accent);
}
.motion-paused .architecture-stage {
  animation-play-state: paused;
}
.hero-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-block: 2.2rem 1.6rem;
  margin-top: 2.5rem;
  color: var(--color-text-muted);
  font-size: 0.8125rem;
}
.hero-scroll {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: var(--color-text-secondary);
  min-height: 44px;
}
.hero-scroll:hover {
  color: var(--color-accent);
}
@keyframes systemStage {
  0%,
  18%,
  100% {
    border-color: var(--color-border);
    background: var(--color-background);
  }
  6%,
  12% {
    border-color: var(--color-accent);
    background: rgb(240 240 237 / 0.07);
    box-shadow: inset 0 0 0 1px rgb(240 240 237 / 0.08);
  }
}

/* Homepage sections */
.audience-strip {
  border-block: 1px solid var(--color-border);
  background: var(--color-surface);
}
.audience-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  padding-block: 1.7rem;
}
.audience-inner > .label {
  font-size: 0.65rem;
  color: var(--color-text-muted);
  max-width: 13rem;
}
.audience-inner > div {
  display: flex;
  gap: clamp(1rem, 4vw, 4rem);
  flex-wrap: wrap;
  color: var(--color-text-secondary);
  font-size: 1.05rem;
  font-weight: 550;
}
.editorial-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(2rem, 6vw, 6rem);
  align-items: center;
}
.editorial-grid > * {
  min-width: 0;
}
.work-shift {
  border-top: 1px solid var(--color-border-strong);
}
.work-shift-label {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding-block: 1.2rem;
  font-size: 0.9rem;
  color: var(--color-accent);
}
.work-shift-row {
  display: grid;
  grid-template-columns: 2rem 1fr 1.2rem;
  gap: 0.75rem;
  align-items: center;
  padding-block: 1.2rem;
  border-top: 1px solid var(--color-border);
  font-size: 1rem;
}
.work-shift-row svg {
  color: var(--color-accent);
}
.work-shift > p {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  margin-top: 1.4rem;
}
.capability-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.2rem;
  margin-top: 3rem;
}
.capability-card {
  display: flex;
  flex-direction: column;
  padding: clamp(1.4rem, 2.5vw, 2rem);
  background: var(--color-background);
}
.capability-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--color-accent);
  margin-bottom: 2.5rem;
}
.capability-card h3 {
  font-size: 1.5rem;
  letter-spacing: -0.035em;
  font-weight: 550;
  line-height: 1.3;
  margin-bottom: 1rem;
}
.capability-card p {
  color: var(--color-text-secondary);
  line-height: 1.75;
  flex: 1;
}
.capability-card .text-link {
  margin-top: 1.6rem;
  font-size: 0.9rem;
}
.section-heading-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
}
.section-heading-row .section-heading {
  max-width: 43rem;
}
.section-heading-row > a {
  flex-shrink: 0;
}
.oversight-panel {
  padding: clamp(1.5rem, 3vw, 2.5rem);
}
.oversight-panel > .label {
  color: var(--color-accent);
}
.oversight-row {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  border-top: 1px solid var(--color-border);
  padding-top: 1.5rem;
  margin-top: 1.5rem;
}
.oversight-row > svg {
  flex-shrink: 0;
  color: var(--color-accent);
}
.oversight-row h3 {
  font-size: 1.25rem;
  line-height: 1.4;
  font-weight: 550;
  letter-spacing: -0.02em;
}
.oversight-row p {
  color: var(--color-text-secondary);
  margin-top: 0.4rem;
  font-size: 0.95rem;
}
.engagement-steps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2.5rem;
  list-style: none;
  padding: 0;
  margin: 3rem 0 0;
}
.engagement-steps li {
  border-top: 1px solid var(--color-border-strong);
  padding-top: 1.5rem;
}
.engagement-steps h3 {
  font-size: 1.35rem;
  font-weight: 550;
  letter-spacing: -0.02em;
  margin-block: 1.2rem 0.65rem;
}
.engagement-steps p {
  color: var(--color-text-secondary);
  font-size: 1rem;
  line-height: 1.7;
}
.faq-home {
  align-items: start;
}
.faq-list {
  border-top: 1px solid var(--color-border);
}
.faq-list details {
  border-bottom: 1px solid var(--color-border);
}
.faq-list summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-block: 1.3rem;
  font-size: 1.05rem;
  font-weight: 550;
  cursor: pointer;
  list-style: none;
}
.faq-list summary::-webkit-details-marker {
  display: none;
}
.faq-list summary svg {
  flex-shrink: 0;
  color: var(--color-accent);
  transition: transform 0.25s;
}
.faq-list details[open] summary svg {
  transform: rotate(180deg);
}
.faq-list details > p {
  color: var(--color-text-secondary);
  padding-bottom: 1.5rem;
  font-size: 1rem;
  line-height: 1.75;
}

/* Aligned comparison, recomposed on mobile */
.comparison {
  margin-top: 3rem;
  border: 1px solid var(--color-border);
  border-radius: 0.75rem;
  overflow: clip;
}
.comparison-head,
.comparison-row {
  display: grid;
  grid-template-columns: 0.85fr 1fr 1.15fr;
}
.comparison-head {
  background: var(--color-surface);
  font-weight: 600;
  font-size: 0.875rem;
}
.comparison-head > span,
.comparison-row > div {
  padding: 1.4rem 1.5rem;
  min-width: 0;
}
.comparison-head > :last-child {
  color: var(--color-accent);
}
.comparison-row {
  border-top: 1px solid var(--color-border);
  font-size: 1.05rem;
  line-height: 1.7;
}
.comparison-dimension {
  font-weight: 550;
  color: var(--color-text-primary);
}
.comparison-manual {
  color: var(--color-text-secondary);
}
.comparison-system {
  background: var(--color-accent-muted);
  color: var(--color-text-primary);
}
.comparison-mobile-label {
  display: none;
}

/* Process cards retain equal alignment and natural reading order */
.pipeline {
  margin-top: 3rem;
}
.pipeline-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.25rem;
  list-style: none;
  padding: 0;
  margin: 0;
  align-items: stretch;
}
.pipeline-card {
  padding: 1.7rem;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: var(--color-background);
}
.pipeline-card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--color-accent);
  margin-bottom: 1.75rem;
}
.pipeline-card h3 {
  font-size: 1.3rem;
  font-weight: 550;
  line-height: 1.35;
  letter-spacing: -0.02em;
  margin-bottom: 0.85rem;
}
.pipeline-card > p {
  color: var(--color-text-secondary);
  font-size: 1rem;
  line-height: 1.75;
  flex: 1;
}
.pipeline-details {
  border-top: 1px solid var(--color-border);
  margin-top: 1.25rem;
  padding-top: 0.85rem;
}
.pipeline-details summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  gap: 0.5rem;
  font-size: 0.875rem;
  min-height: 44px;
  color: var(--color-accent);
  list-style: none;
}
.pipeline-details ul {
  list-style: none;
  padding: 0;
  margin: 0.75rem 0 0;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
}
.pipeline-details li {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin-top: 0.6rem;
}
.pipeline-details svg {
  flex-shrink: 0;
  color: var(--color-accent);
  margin-top: 0.3rem;
}
.pipeline-feedback {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  border-top: 1px solid var(--color-border);
  padding-top: 1.5rem;
  margin-top: 1.5rem;
  color: var(--color-text-secondary);
  font-size: 0.95rem;
}
.pipeline-feedback svg {
  color: var(--color-accent);
  flex-shrink: 0;
}
.pipeline-principles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.5rem;
  border-top: 1px solid var(--color-border);
  padding-top: 2rem;
  margin-top: 2rem;
}
.pipeline-principles h3 {
  font-size: 1rem;
  font-weight: 550;
  color: var(--color-accent);
}
.pipeline-principles p {
  font-size: 0.95rem;
  color: var(--color-text-secondary);
  margin-top: 0.5rem;
}
.lifecycle-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.25rem;
  margin-top: 3rem;
}
.lifecycle-card {
  padding: 1.7rem;
}
.lifecycle-card > .step-number {
  display: block;
  margin-bottom: 1rem;
}
.lifecycle-card h3 {
  margin-bottom: 0.6rem;
}
.lifecycle-card p {
  color: var(--color-text-secondary);
}

/* Inner pages and one purposeful closing CTA */
.page-hero {
  padding-block: clamp(4.5rem, 7vw, 7rem);
  border-bottom: 1px solid var(--color-border);
  background: radial-gradient(
    ellipse at 50% 0%,
    rgb(240 240 237 / 0.05),
    transparent 70%
  );
}
.page-hero-inner {
  max-width: 56rem;
  margin-inline: auto;
  text-align: center;
}
.page-hero-inner h1 {
  font-size: clamp(2.3rem, 5vw, 4rem);
  line-height: 1.12;
  letter-spacing: -0.045em;
  font-weight: 550;
  text-wrap: balance;
  margin-top: 1.5rem;
}
.page-hero-inner > p {
  max-width: 43rem;
  margin: 1.5rem auto 0;
  font-size: 1.125rem;
  line-height: 1.8;
  color: var(--color-text-secondary);
}
.page-hero-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2rem;
}
.booking-cta {
  background: var(--color-surface);
  border-top: 1px solid var(--color-border);
}
.booking-cta-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 3rem;
}
.booking-cta-inner h2 {
  margin-top: 1.2rem;
  font-size: clamp(2.2rem, 4.3vw, 3.5rem);
  font-weight: 550;
  line-height: 1.15;
  letter-spacing: -0.045em;
}
.booking-cta-inner p {
  color: var(--color-text-secondary);
  max-width: 36rem;
  font-size: 1.125rem;
  margin-top: 1.5rem;
}
.booking-cta-action {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}
.booking-cta-action > span {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
}

/* Footer content has its own grid and stays separate from conversion content */
.site-footer {
  border-top: 1px solid var(--color-border);
  padding-top: 4rem;
}
.footer-grid {
  display: grid;
  grid-template-columns: 1.6fr 0.8fr 1fr 0.8fr;
  gap: 3rem;
}
.footer-brand > p {
  color: var(--color-text-secondary);
  max-width: 22rem;
  font-size: 0.95rem;
  margin-top: 1rem;
  line-height: 1.75;
}
.footer-brand .footer-positioning {
  color: var(--color-text-primary);
  font-weight: 550;
  margin-top: 1.5rem;
}
.footer-contact {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin-top: 1.3rem;
  min-height: 44px;
}
.footer-grid nav h2 {
  color: var(--color-text-primary);
  margin-bottom: 1.3rem;
  font-size: 0.7rem;
}
.footer-grid ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.footer-grid li + li {
  margin-top: 0.5rem;
}
.footer-grid nav a {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  min-height: 36px;
}
.site-footer a:hover {
  color: var(--color-accent);
}
.footer-bottom {
  border-top: 1px solid var(--color-border);
  margin-top: 3.5rem;
  padding-block: 1.6rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  color: var(--color-text-muted);
  font-size: 0.8rem;
}
.footer-bottom nav {
  display: flex;
  gap: 1.5rem;
}
.footer-bottom a {
  color: var(--color-text-secondary);
  min-height: 44px;
  display: inline-flex;
  align-items: center;
}

/* Progressive motion: content is readable even without JavaScript */
.motion-ready {
  animation: none;
  transition:
    opacity 0.6s ease,
    transform 0.6s ease;
}
.motion-ready:not(.is-revealed) {
  opacity: 0.55;
  transform: translateY(16px);
}
.motion-ready.is-revealed {
  opacity: 1;
  transform: none;
}
@media (max-width: 1100px) {
  .hero-grid {
    gap: 2.5rem;
  }
  .hero-title {
    font-size: clamp(2.7rem, 5vw, 3.8rem);
  }
  .footer-grid {
    gap: 1.8rem;
  }
  .section-heading-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 1rem;
  }
}
@media (max-width: 900px) {
  .hero-grid {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
  .hero-copy {
    max-width: 42rem;
  }
  .hero-title {
    font-size: clamp(2.8rem, 7.5vw, 4rem);
  }
  .hero-title-break {
    display: none;
  }
  .hero-visual {
    max-width: 42rem;
    width: 100%;
  }
  .editorial-grid {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
  .capability-grid {
    grid-template-columns: 1fr;
  }
  .capability-top {
    margin-bottom: 1.5rem;
  }
  .pipeline-grid,
  .lifecycle-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .audience-inner {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  .audience-inner > .label {
    max-width: none;
  }
  .booking-cta-inner {
    align-items: flex-start;
    flex-direction: column;
    gap: 2rem;
  }
  .booking-cta-action {
    align-items: flex-start;
  }
  .footer-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .footer-brand {
    grid-column: 1 / -1;
  }
  .pipeline-principles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 640px) {
  .hero-stage {
    padding-top: 2.5rem;
  }
  .hero-title {
    font-size: clamp(2.55rem, 9.8vw, 3.7rem);
  }
  .hero-kicker {
    font-size: 0.65rem;
    letter-spacing: 0.1em;
  }
  .hero-description {
    font-size: 1.05rem;
  }
  .hero-actions {
    gap: 0.7rem 1.25rem;
  }
  .hero-note {
    font-size: 0.8rem;
  }
  .architecture-header {
    margin-bottom: 1.2rem;
  }
  .architecture-badge {
    display: none;
  }
  .architecture-stage {
    padding: 0.85rem;
  }
  .architecture-stage h2 {
    font-size: 0.875rem;
  }
  .hero-bottom {
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 1.5rem;
    padding-block: 1.5rem;
  }
  .hero-bottom > span {
    display: none;
  }
  .audience-inner > div {
    gap: 0.75rem 1.5rem;
    font-size: 0.95rem;
  }
  .pipeline-grid,
  .lifecycle-grid {
    grid-template-columns: 1fr;
  }
  .pipeline-card {
    padding: 1.5rem;
  }
  .pipeline-card-top {
    margin-bottom: 1rem;
  }
  .comparison-head {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
  .comparison-row {
    grid-template-columns: 1fr;
    padding: 1.3rem;
    gap: 1rem;
  }
  .comparison-row:first-of-type {
    border-top: none;
  }
  .comparison-row > div {
    padding: 0;
  }
  .comparison-row > .comparison-system {
    padding: 1rem;
    border-radius: 0.4rem;
  }
  .comparison-dimension {
    font-size: 1.2rem;
  }
  .comparison-mobile-label {
    display: block;
    margin-bottom: 0.4rem;
    color: var(--color-text-muted);
    font-size: 0.7rem;
    letter-spacing: 0.08em;
    font-weight: 600;
    text-transform: uppercase;
  }
  .comparison-system .comparison-mobile-label {
    color: var(--color-accent);
  }
  .engagement-steps {
    grid-template-columns: 1fr;
    gap: 1.75rem;
  }
  .engagement-steps h3 {
    margin-top: 0.8rem;
  }
  .footer-grid {
    grid-template-columns: 1fr 1fr;
    gap: 2.5rem 1.5rem;
  }
  .footer-bottom {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  .page-hero-actions {
    flex-direction: column;
    align-items: stretch;
  }
  .page-hero-inner > .label {
    font-size: 0.65rem;
  }
  .page-hero-inner > p {
    font-size: 1.05rem;
  }
  .pipeline-feedback {
    align-items: flex-start;
    justify-content: flex-start;
  }
  .pipeline-feedback svg {
    margin-top: 0.25rem;
  }
  .pipeline-principles {
    gap: 1.5rem 1rem;
  }
}
@media (prefers-reduced-motion: reduce) {
  .motion-ready:not(.is-revealed) {
    opacity: 1;
    transform: none;
  }
  .architecture-stage {
    animation: none;
  }
  .architecture-footer button {
    display: none;
  }
  .feature-link:hover svg,
  .text-link:hover svg {
    transform: none;
  }
}
.site-header {
  position: fixed;
  inset: 0 0 auto;
  z-index: 300;
  background: rgb(0 0 0 / 0.72);
  backdrop-filter: blur(18px);
  border-bottom: 1px solid var(--color-border);
}
.nav-inner {
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
}
.desktop-nav {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}
.desktop-nav > a,
.nav-technology > button {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  min-height: 44px;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
}
.desktop-nav > a:hover,
.nav-technology > button:hover,
.desktop-nav [aria-current],
.nav-technology .is-active {
  color: var(--color-accent);
}
.nav-technology {
  position: relative;
}
.nav-technology > button {
  background: transparent;
  border: 0;
  padding: 0;
}
.nav-dropdown {
  position: absolute;
  left: -1rem;
  top: 100%;
  width: 265px;
  padding: 0.5rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border-strong);
  border-radius: 0.6rem;
  box-shadow: 0 12px 36px rgb(0 0 0 / 0.3);
  animation: slideDown 0.2s ease;
}
.nav-dropdown a {
  display: block;
  padding: 0.8rem;
  border-radius: 0.3rem;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}
.nav-dropdown a:hover,
.nav-dropdown a[aria-current] {
  background: var(--color-accent-muted);
  color: var(--color-accent);
}
.nav-cta {
  flex-shrink: 0;
}
.mobile-menu-toggle {
  display: none;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  background: rgb(255 255 255 / 0.05);
  backdrop-filter: blur(14px);
  border: 1px solid var(--color-border);
  border-radius: 0.5rem;
  color: var(--color-text-primary);
}
.mobile-navigation {
  display: none;
}
.skip-link {
  position: fixed;
  top: 0.75rem;
  left: 1rem;
  padding: 0.8rem 1rem;
  background: var(--color-accent);
  color: var(--color-accent-foreground);
  border-radius: 0.4rem;
  z-index: 400;
  transform: translateY(-160%);
}
.skip-link:focus {
  transform: none;
}
@media (max-width: 1179px) {
  .desktop-nav {
    display: none;
  }
  .mobile-menu-toggle {
    display: inline-flex;
  }
  .nav-cta {
    margin-left: auto;
  }
  .mobile-navigation {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding-block: 0.75rem 1.25rem;
    max-height: calc(100dvh - 80px);
    overflow-y: auto;
    border-top: 1px solid var(--color-border);
    animation: slideDown 0.2s;
  }
  .mobile-navigation > a:not(.btn) {
    padding: 0.6rem 0.4rem;
    min-height: 44px;
    color: var(--color-text-secondary);
    font-size: 1rem;
  }
  .mobile-navigation > a[aria-current],
  .mobile-navigation > a:hover {
    color: var(--color-accent);
  }
  .mobile-navigation .label {
    color: var(--color-accent);
    padding: 1rem 0.4rem 0.3rem;
  }
  .mobile-navigation .btn {
    align-self: flex-start;
    margin-top: 0.6rem;
  }
}
@media (max-width: 640px) {
  .nav-inner {
    height: 64px;
  }
  .nav-cta {
    display: none;
  }
  .mobile-navigation {
    max-height: calc(100dvh - 64px);
  }
}
@media (max-width: 767px) {
  .nav-inner {
    height: 64px;
  }
  .mobile-navigation {
    max-height: calc(100dvh - 64px);
  }
}
.contact-layout {
  align-items: start;
}
.contact-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: clamp(1.3rem, 3vw, 2.5rem);
}
.contact-form > button {
  align-self: flex-start;
}
.lifecycle-grid {
  list-style: none;
  padding: 0;
}

.booking-layout {
  grid-template-columns: minmax(0, 1fr);
}
.booking-layout > * {
  min-width: 0;
}
@media (min-width: 1024px) {
  .booking-layout {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.form-navigation {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.form-navigation .btn {
  padding-inline: 1rem;
}
fieldset {
  min-inline-size: 0;
}

.responsibility-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;
  margin-top: 3rem;
}
.responsibility-card {
  padding: 1.7rem;
}
.responsibility-card > p {
  margin-top: 0.75rem;
}
.responsibility-card dl {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  border-top: 1px solid var(--color-border);
  padding-top: 1rem;
  margin-top: 1.5rem;
}
.responsibility-card dt {
  color: var(--color-text-muted);
  font-size: 0.8rem;
}
.responsibility-card dd {
  color: var(--color-accent);
  font-size: 1rem;
  margin-top: 0.5rem;
}
@media (max-width: 640px) {
  .responsibility-grid {
    grid-template-columns: 1fr;
  }
}

.brand-mark {
  display: block;
  flex-shrink: 0;
  width: 58px;
  height: 30px;
  object-fit: contain;
}

/* Categorical service coverage and schematic workflow comparisons. */
.service-comparison-charts {
  display: grid;
  gap: 2rem;
  margin-top: 3rem;
}
.service-chart {
  margin: 0;
  border: 1px solid var(--color-border);
  border-radius: 1rem;
  overflow: hidden;
  background: #080c0e;
}
.service-chart-heading {
  padding: clamp(1.3rem, 3vw, 2rem);
}
.service-chart-heading > .label {
  color: var(--color-text-muted);
}
.service-chart-heading h3 {
  font-size: clamp(1.4rem, 2.3vw, 1.9rem);
  line-height: 1.3;
  letter-spacing: -0.025em;
  font-weight: 500;
  margin-top: 0.9rem;
}
.service-chart-heading p {
  color: var(--color-text-secondary);
  font-size: 1rem;
  line-height: 1.7;
  margin-top: 0.8rem;
  max-width: 50rem;
}
.scope-chart-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  text-align: left;
}
.scope-chart-table thead {
  background: #101618;
}
.scope-chart-table th,
.scope-chart-table td {
  padding: 1.3rem 0.75rem;
  vertical-align: middle;
}
.scope-chart-table th:first-child {
  width: 23%;
  padding-left: 2rem;
}
.scope-chart-table thead th {
  font-size: 0.85rem;
  line-height: 1.5;
  font-weight: 500;
  color: var(--color-text-secondary);
}
.scope-chart-table tbody tr {
  border-top: 1px solid var(--color-border);
}
.scope-chart-table tbody th > span {
  font-size: 1.05rem;
  font-weight: 550;
}
.scope-chart-table tbody th > p {
  font-size: 0.85rem;
  font-weight: 400;
  line-height: 1.6;
  color: var(--color-text-secondary);
  margin-top: 0.6rem;
}
.scope-mark {
  display: inline-flex;
  flex-direction: column;
  gap: 0.45rem;
  align-items: center;
  justify-content: center;
  width: 100%;
  font-size: 0.75rem;
  line-height: 1.5;
  text-align: center;
}
.scope-mark svg {
  box-sizing: content-box;
  border: 1px solid #596267;
  border-radius: 50%;
  padding: 0.45rem;
}
.scope-mark.is-outside {
  color: var(--color-text-muted);
}
.scope-mark.is-outside svg {
  border-style: dashed;
  border-color: #434b50;
}
.scope-mark.is-included {
  color: var(--color-text-primary);
}
.scope-mark.is-included svg {
  background: #222e33;
  border-color: #8c9ba2;
}
.scope-model-connected {
  background: #142024;
}
.scope-model-connected th {
  border-left: 3px solid #d7e5e8;
}
.scope-model-connected .is-included svg {
  background: #e0edef;
  color: #101a1e;
  border-color: #e0edef;
}
.scope-mobile-stage {
  display: none;
}
.service-chart-note {
  font-size: 0.85rem;
  line-height: 1.7;
  color: var(--color-text-secondary);
  padding: 1.25rem clamp(1.3rem, 3vw, 2rem);
  border-top: 1px solid var(--color-border);
}
.workflow-comparison {
  padding: 0 clamp(1.3rem, 3vw, 2rem) 2rem;
  display: grid;
  gap: 1.5rem;
}
.workflow-lane {
  border: 1px solid var(--color-border);
  border-radius: 0.75rem;
  padding: 1.5rem;
}
.workflow-lane-connected {
  background: #142024;
  border-color: #56686e;
}
.workflow-lane-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.workflow-lane-heading h4 {
  font-size: 1.1rem;
  font-weight: 550;
}
.workflow-lane-heading p {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}
.workflow-path {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 2rem;
  padding: 0;
  margin: 1.6rem 0 0;
  list-style: none;
}
.workflow-path li {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.55rem;
  border: 1px solid var(--color-border-strong);
  background: #080c0e;
  border-radius: 0.6rem;
  padding: 1rem;
  min-width: 0;
}
.workflow-node-index {
  font-size: 0.75rem;
  color: var(--color-text-muted);
}
.workflow-path strong {
  font-size: 1.05rem;
  font-weight: 500;
}
.workflow-path li > span:not(.workflow-node-index):not(.workflow-direction) {
  font-size: 0.85rem;
  line-height: 1.55;
  color: var(--color-text-secondary);
}
.workflow-direction {
  position: absolute;
  left: calc(100% + 0.5rem);
  top: 50%;
  transform: translateY(-50%);
  width: 1rem;
  color: var(--color-text-secondary);
}
.workflow-arrow-vertical {
  display: none;
}
.workflow-return,
.workflow-end {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-top: 1.2rem;
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--color-text-secondary);
}
.workflow-return {
  border: 1px solid #738b92;
  border-top: 0;
  border-radius: 0 0 0.6rem 0.6rem;
  padding: 0.8rem 1rem;
  justify-content: center;
  color: #e0edef;
}
.workflow-return svg {
  flex-shrink: 0;
}
.workflow-end-dot {
  width: 0.6rem;
  height: 0.6rem;
  background: #929ea3;
  border-radius: 50%;
  flex-shrink: 0;
}
@media (max-width: 900px) {
  .scope-chart-table {
    display: block;
  }
  .scope-chart-table thead {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
  .scope-chart-table tbody {
    display: grid;
    gap: 1rem;
    padding: 0 1.3rem 1.3rem;
  }
  .scope-chart-table tbody tr {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    border: 1px solid var(--color-border);
    border-radius: 0.6rem;
    overflow: hidden;
  }
  .scope-chart-table tbody th:first-child {
    grid-column: 1 / -1;
    width: auto;
    padding: 1.3rem;
    border-bottom: 1px solid var(--color-border);
  }
  .scope-chart-table tbody td {
    padding: 1rem 0.6rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .scope-mobile-stage {
    display: block;
    font-size: 0.85rem;
    line-height: 1.5;
    text-align: center;
    min-height: 2.55rem;
  }
  .workflow-path {
    gap: 1.5rem;
  }
  .workflow-path li {
    padding: 0.8rem;
  }
  .workflow-direction {
    left: calc(100% + 0.25rem);
  }
}
@media (max-width: 640px) {
  .scope-chart-table tbody tr {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .scope-mobile-stage {
    min-height: auto;
  }
  .workflow-lane {
    padding: 1.1rem;
  }
  .workflow-path {
    grid-template-columns: 1fr;
    gap: 1.6rem;
  }
  .workflow-path li {
    padding: 1rem;
  }
  .workflow-direction {
    top: calc(100% + 0.3rem);
    left: 50%;
    transform: translateX(-50%);
  }
  .workflow-arrow-horizontal {
    display: none;
  }
  .workflow-arrow-vertical {
    display: block;
  }
}

/* Cinematic reference adapted to the existing UniqReach content. */
:root {
  color-scheme: dark;
}
.cinematic-hero {
  min-height: min(940px, calc(100svh - 80px));
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: radial-gradient(
    ellipse at 75% 30%,
    #173039 0%,
    #071015 40%,
    #000 78%
  );
  padding-top: 7rem;
}
.hero-background-video {
  position: absolute;
  inset: 0;
  z-index: -3;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  pointer-events: none;
  user-select: none;
}
.cinematic-hero::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -2;
  background:
    linear-gradient(90deg, rgb(0 0 0 / 0.82), rgb(0 0 0 / 0.18)),
    linear-gradient(
      180deg,
      rgb(0 0 0 / 0.08),
      transparent 24%,
      rgb(0 0 0 / 0.5) 82%,
      #000
    ),
    radial-gradient(
      ellipse at 44% 54%,
      transparent 30%,
      rgb(0 0 0 / 0.055) 100%
    );
  pointer-events: none;
}
.cinematic-hero .hero-grid {
  align-items: end;
  grid-template-columns: 1.2fr 1fr;
  gap: clamp(2rem, 4vw, 4rem);
}
.cinematic-hero .hero-title {
  font-family: "Barlow Condensed", "DM Sans", Arial, sans-serif;
  font-weight: 500;
  font-optical-sizing: auto;
  font-size: clamp(3.7rem, 6.1vw, 6.4rem);
  line-height: 1.04;
  letter-spacing: -2.1px;
  -webkit-text-stroke: 0.12px currentColor;
  text-shadow: 0 2px 2px rgb(0 0 0 / 0.44);
}
.hero-line {
  display: block;
  overflow: clip;
  color: #fafafa;
}
.cinematic-hero .hero-line-muted {
  color: rgb(211 207 207 / 0.88);
}
.hero-line > span {
  display: block;
  animation: cinematic-line 850ms cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: 300ms;
}
.hero-line-muted > span {
  animation-delay: 440ms;
}
.cinematic-hero .hero-kicker {
  animation: cinematic-enter 580ms cubic-bezier(0.16, 1, 0.3, 1) both;
  animation-delay: 60ms;
}
.cinematic-hero .hero-description {
  font-weight: 350;
  letter-spacing: 0.13px;
  color: #d9dedd;
  text-shadow: 0 1px 2px #000;
  animation: cinematic-enter 620ms cubic-bezier(0.16, 1, 0.3, 1) both;
  animation-delay: 740ms;
}
.cinematic-hero .hero-actions {
  animation: cinematic-enter 560ms cubic-bezier(0.16, 1, 0.3, 1) both;
  animation-delay: 960ms;
}
.cinematic-hero .hero-visual {
  animation: cinematic-enter 920ms cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: 1040ms;
  border-color: rgb(255 255 255 / 0.2);
}
.cinematic-hero .hero-bottom {
  margin-top: 3rem;
}
.cinematic-hero .hero-title-break {
  display: none;
}
.hero-actions .btn-primary {
  border-radius: 7px;
  padding-right: 0.45rem;
  gap: 1rem;
  box-shadow: 0 2px 3px rgb(0 0 0 / 0.3);
}
.hero-actions .btn-primary > svg {
  background: #101719;
  color: white;
  padding: 8px;
  width: 34px;
  height: 34px;
  border-radius: 7px;
}
.btn-primary:hover:not(:disabled) {
  filter: brightness(1.08);
}
.page-hero {
  background: radial-gradient(
    ellipse at 50% 0%,
    rgb(115 151 163 / 0.1),
    transparent 70%
  );
}
.system-counts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  padding-bottom: 1.7rem;
  margin-bottom: 1.7rem;
  border-bottom: 1px solid var(--color-border);
}
.system-counts > div {
  display: flex;
  flex-direction: column-reverse;
  gap: 0.5rem;
}
.system-counts dt {
  font-size: 0.8rem;
  color: var(--color-text-secondary);
}
.system-counts dd {
  font-size: clamp(2.2rem, 4vw, 3rem);
  line-height: 1;
  letter-spacing: -0.05em;
}
.measurement-table-wrap {
  margin-top: 3rem;
  border: 1px solid var(--color-border);
  border-radius: 1rem;
  overflow: hidden;
  background: linear-gradient(145deg, #11181b, #06090a);
}
.measurement-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}
.measurement-table thead {
  background: rgb(255 255 255 / 0.04);
}
.measurement-table th,
.measurement-table td {
  padding: 1.6rem;
  vertical-align: top;
}
.measurement-table thead th {
  font-size: 0.8rem;
  letter-spacing: 0.04em;
  color: var(--color-text-secondary);
  font-weight: 500;
}
.measurement-table tbody th {
  width: 36%;
  font-size: 1.15rem;
  font-weight: 500;
}
.measurement-table tbody th > div {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.measurement-table svg {
  flex-shrink: 0;
}
.measurement-table td {
  font-size: 1.05rem;
  line-height: 1.75;
  color: var(--color-text-secondary);
}
.measurement-table tbody tr {
  border-top: 1px solid var(--color-border);
}
.review-flow .work-shift-row {
  position: relative;
  min-height: 85px;
  border: 0;
}
.review-flow .work-shift-row::before {
  content: "";
  position: absolute;
  left: 19px;
  top: 50%;
  height: 100%;
  width: 1px;
  background: var(--color-border-strong);
}
.review-flow .work-shift-row:last-child::before {
  display: none;
}
.review-node {
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: 1px solid var(--color-border-strong);
  border-radius: 50%;
  background: #10191d;
  font-size: 0.8rem;
}
.review-flow .work-shift-row > svg {
  display: none;
}
@keyframes cinematic-line {
  from {
    transform: translateY(110%) skewY(2deg);
  }
  to {
    transform: none;
  }
}
@keyframes cinematic-enter {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@media (max-width: 1100px) {
  .cinematic-hero .hero-title {
    font-size: clamp(3.5rem, 6.5vw, 5rem);
  }
}
@media (max-width: 900px) {
  .cinematic-hero {
    padding-top: 4rem;
  }
  .cinematic-hero .hero-grid {
    grid-template-columns: 1fr;
  }
  .cinematic-hero .hero-visual {
    max-width: none;
  }
  .cinematic-hero .hero-title {
    font-size: clamp(3.5rem, 9vw, 5.5rem);
  }
}
@media (max-width: 640px) {
  .cinematic-hero {
    padding-top: 3rem;
    min-height: calc(100svh - 64px);
  }
  .cinematic-hero .hero-title {
    font-size: clamp(3rem, 11.7vw, 4.5rem);
    letter-spacing: -1.3px;
  }
  .cinematic-hero .hero-bottom {
    margin-top: 1.5rem;
  }
  .cinematic-hero::before {
    background: linear-gradient(
      180deg,
      rgb(0 0 0 / 0.5),
      rgb(0 0 0 / 0.6) 55%,
      #000
    );
  }
  .measurement-table th,
  .measurement-table td {
    padding: 1rem 0.85rem;
  }
  .measurement-table tbody th {
    font-size: 0.95rem;
    width: 38%;
  }
  .measurement-table tbody th > div {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  .measurement-table td {
    font-size: 0.95rem;
  }
  .system-counts {
    gap: 0.6rem;
  }
}
@media (prefers-reduced-motion: reduce) {
  .cinematic-hero *,
  .hero-line > span {
    animation: none !important;
  }
}
@media (max-width: 640px) {
  .brand-mark {
    width: 49px;
    height: 27px;
  }
}
